/**
 * -----------------------------------------------------------------------------
 * useMap
 * -----------------------------------------------------------------------------
 *
 * PURPOSE:
 * Unified hook that initializes a Mapbox map AND manages the MapboxDraw control.
 * Merges useMapInit + useMapDraw to eliminate timing issues caused by mapRef
 * not being ready when useMapDraw tries to consume it.
 *
 * WHAT IT HANDLES:
 *   - Mapbox map initialization
 *   - Style switching
 *   - MapboxDraw control setup (polygon, rectangle, circle)
 *   - draw.create / draw.update event wiring
 *   - Loading existing geometry into draw for editing
 *   - Full cleanup on unmount
 *
 * WHAT IT DOES NOT HANDLE:
 *   - Saving geometry to backend
 *   - Geometry size/area validation
 *   - Controlling draw modes externally (use returned drawRef)
 *
 * EXAMPLE USAGE:
 *
 * const { mapRef, drawRef } = useMap({
 *   containerRef,
 *   style,
 *   center,
 *   zoom,
 *   projection,
 *   onLoad,
 *   analysisMode,
 *   existingGeometry,
 *   dispatchDrawRef: (draw) => dispatch(setDrawRef(draw)),
 *   onAOIChange: (geom) => dispatch(setGeometryDrawn(geom)),
 *   onMultiAOIChange: (collection) => dispatch(setMultiAOICoordinates(collection)),
 * });
 *
 * -----------------------------------------------------------------------------
 */

import { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import DrawRectangle from "mapbox-gl-draw-rectangle-mode";
import { CircleMode, DragCircleMode } from "mapbox-gl-draw-circle";

const useMap = ({
  // --- Map init params ---
  containerRef,
  style,
  center,
  zoom,
  projection,
  onLoad,

  // --- Draw params ---
  analysisMode,
  existingGeometry,
  onAOIChange,
  onMultiAOIChange,
  dispatchDrawRef,
}) => {
  const mapRef = useRef(null);
  const drawRef = useRef(null);
  const analysisModeRef = useRef(analysisMode);
  const onAOIChangeRef = useRef(onAOIChange);
  const onMultiAOIChangeRef = useRef(onMultiAOIChange);

  useEffect(() => {
    analysisModeRef.current = analysisMode;
  }, [analysisMode]);

  useEffect(() => {
    onAOIChangeRef.current = onAOIChange;
    onMultiAOIChangeRef.current = onMultiAOIChange;
  }, [onAOIChange, onMultiAOIChange]);


  /**
   * Initialize map + draw together so there's no timing gap between them.
   * Draw is added inside map's "load" event so the map is guaranteed ready.
   */
  useEffect(() => {
    if (mapRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style,
      center,
      zoom,
      projection: { name: projection },
    });

    mapRef.current = map;

    map.on("load", () => {
      // --- Initialize Draw ---
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        userProperties: true,
        modes: {
          ...MapboxDraw.modes,
          draw_circle: CircleMode,
          drag_circle: DragCircleMode,
          draw_rectangle: DrawRectangle,
        },
      });

      map.addControl(draw);
      drawRef.current = draw;
      dispatchDrawRef?.(draw);

      // --- Wire draw events ---
      const updateCoordinates = () => {
        const drawn = draw.getAll();
        if (!drawn?.features) return;

        if (!analysisModeRef.current && drawn.features.length > 0) {
          const feature = drawn.features[0];
          if (feature?.geometry?.type) {
            onAOIChangeRef.current?.(feature.geometry);
          }
        } else {
          onMultiAOIChangeRef.current?.(drawn);
        }
      };

      map.on("draw.create", updateCoordinates);
      map.on("draw.update", updateCoordinates);

      // Expose cleanup on the draw ref so the main cleanup can reach it
      drawRef.current._updateCoordinates = updateCoordinates;

      // --- Call external onLoad after draw is ready ---
      onLoad?.(map, draw);
    });

    return () => {
      const draw = drawRef.current;
      if (draw) {
        const updateCoordinates = draw._updateCoordinates;
        if (updateCoordinates) {
          map.off("draw.create", updateCoordinates);
          map.off("draw.update", updateCoordinates);
        }
        draw.deleteAll();
        map.removeControl(draw);
        drawRef.current = null;
      }

      map.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // ^ Intentionally empty: map + draw are initialized once for the lifetime
  //   of the component. Style changes are handled in the effect below.

  /**
   * Handle style changes without reinitializing the map.
   * Draw control survives style changes because it's re-added after the
   * style.load event fires — Mapbox handles this automatically for controls.
   */
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setStyle(style);
  }, [style]);

  /**
   * Load or replace existing geometry into draw for editing.
   * Runs whenever existingGeometry changes.
   */
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw) return;

    if (!existingGeometry?.type || !existingGeometry?.coordinates) {
      return;
    }

    try {
      draw.deleteAll();
      draw.add({
        type: "Feature",
        geometry: existingGeometry,
        properties: {},
      });
    } catch (err) {
      console.warn("Invalid geometry passed to draw:", err);
    }
  }, [existingGeometry]);

  return { mapRef, drawRef };
};

export default useMap;
